import { IPassage } from '../../models/Passage';
import { IPassageWaveformSummary } from '../../models/PassageWaveform';
import DeviceService from '../../services/DeviceService';

export interface PassageState {
  selectedPassageTimestamp: string;
  selectedPassages: IPassage[];
  passageDialogSource: string;
  passageIdsToUpdate: number[];
  uidToUpdate: number;
  machineLearningPassageResultList: any[];
}

export default {
  state: {
    selectedPassageTimestamp: '',
    selectedPassages: [] as IPassage[],
    passageDialogSource: 'selected_passage',
    passageIdsToUpdate: [] as number[],
    uidToUpdate: 0,
    machineLearningPassageResultList: [],
  } as PassageState,
  mutations: {
    SET_SELECTED_PASSAGE_TIMESTAMP(state: PassageState, timestamp: string) {
      state.selectedPassageTimestamp = timestamp;
    },
    SET_SELECTED_PASSAGES(state: PassageState, passages: IPassage[]) {
      state.selectedPassages = passages;
    },
    SET_PASSAGE_DIALOG_SOURCE(state: PassageState, source: string) {
      state.passageDialogSource = source;
    },
    ADD_PASSAGE_IDS_TO_UPDATE(state: PassageState, passageIds: number[]) {
      state.passageIdsToUpdate = passageIds;
    },
    ADD_UID_TO_UPDATE(state: PassageState, uid: number) {
      state.uidToUpdate = uid;
    },
    SET_MACHINE_LEARNING_PASSAGE_RESULT_LIST(
      state: PassageState,
      payload: any
    ) {
      state.machineLearningPassageResultList = payload;
    },
  },
  actions: {
    setPassageDialogSource(context: any, payload: string) {
      context.commit('SET_PASSAGE_DIALOG_SOURCE', payload);
    },
    clearPassageIdsToUpdate(context: any) {
      context.commit('ADD_PASSAGE_IDS_TO_UPDATE', []);
    },
    addPassageIdsToUpdate(context: any, passageIds: number[]) {
      context.commit('ADD_PASSAGE_IDS_TO_UPDATE', passageIds);
    },
    addUIDToUpdate(context: any, uid: number) {
      context.commit('ADD_UID_TO_UPDATE', uid);
    },
    updateLastPassage(context: any) {
      const selectedDevice = context.rootState.device.selectedDevice;
      if (!selectedDevice) return;
      DeviceService.getLastPassageData(selectedDevice.uid)
        .then((response: any) => {
          if (response.data && response.data.length) {
            // all last passages will have identical DC and times
            context.commit(
              'SET_DEVICE_PASSAGE_DETECTION_COUNT',
              response.data[0].detectionCount,
              { root: true }
            );
            context.commit(
              'SET_DEVICE_PASSAGE_TIMESTAMP',
              response.data[0].passageTimeStamp,
              { root: true }
            );
            const newSelectedPassages = [];
            for (const passage of response.data) {
              if (passage.packetComplexInformation === 0) {
                passage.elfId = passage.passageId;
              }
              if (passage.packetComplexInformation === 1) {
                passage.geoId = passage.passageId;
              }
              if (passage.packetComplexInformation === 2) {
                passage.magId = passage.passageId;
              }
              newSelectedPassages.push(passage);
            }
            // context.commit('SET_LAST_PASSAGES', newSelectedPassages); // \todo: throws error
          }
        })
        .catch((error: any) => {
          console.error(error);
          context.dispatch('notify', error);
        });
    },
    passageChartShow() {
      // Signal-only action – PassageDialog subscribes and handles the fetch locally.
    },
    passageDownload() {
      // No longer used – DeviceHistory fetches waveform data directly.
    },
    passageExportCsv() {
      // No longer used – DeviceHistory calls onPassageExport directly.
    },
    verifyPassage(
      context: any,
      payload: { passage: any; passageWaveformId: number }
    ) {
      context.dispatch('togglePassageVerification', payload);
    },
    togglePassageVerification(
      context: any,
      payload: { passage: any; passageWaveformId: number }
    ) {
      const { passage, passageWaveformId } = payload;
      if (!passageWaveformId || !passage.uid) {
        console.error('Missing passageWaveformId or uid');
        return;
      }

      const wf = (passage.passageWaveforms || []).find(
        (w: IPassageWaveformSummary) =>
          w.passageWaveformId === passageWaveformId
      );
      if (!wf) {
        console.error('Waveform not found:', passageWaveformId);
        return;
      }

      const newVerified = !wf.isVerified;
      wf.isVerified = newVerified;

      DeviceService.setPassageWaveformVerification(
        passage.uid,
        passageWaveformId,
        newVerified
      )
        .then(() => {
          if (passage.markerName !== undefined) {
            context.dispatch('verifyMarkerPassageComplete', passage);
          } else {
            context.dispatch('verifyPassageComplete', passage);
            if (context.state.passageDialogSource === 'report_manager') {
              return;
            }
            context.dispatch('updateLastPassage');
          }
        })
        .catch((error: any) => {
          // Revert on failure
          wf.isVerified = !newVerified;
          console.error(error);
          context.dispatch('notify', error);
        });
    },
    unverifyAllPassageWaveforms(context: any, payload: { passage: any }) {
      const { passage } = payload;
      const waveforms: IPassageWaveformSummary[] =
        passage.passageWaveforms || [];

      const verifiedWaveforms = waveforms.filter((wf) => wf.isVerified);
      if (verifiedWaveforms.length === 0) {
        return;
      }

      // Set all to unverified locally
      for (const wf of verifiedWaveforms) {
        wf.isVerified = false;
      }

      const promises = verifiedWaveforms.map((wf) =>
        DeviceService.setPassageWaveformVerification(
          passage.uid!,
          wf.passageWaveformId,
          false
        )
      );

      Promise.all(promises)
        .then(() => {
          if (passage.markerName !== undefined) {
            context.dispatch('verifyMarkerPassageComplete', passage);
          } else {
            context.dispatch('verifyPassageComplete', passage);
            if (context.state.passageDialogSource === 'report_manager') {
              return;
            }
            context.dispatch('updateLastPassage');
          }
        })
        .catch((error: any) => {
          console.error('unverifyAllPassageWaveforms error:', error);
          context.dispatch('notify', error);
        });
    },
    setPassageWaveformVerification(
      context: any,
      payload: { passage: any; passageWaveformId: number }
    ) {
      const { passage, passageWaveformId } = payload;
      const wf = (passage.passageWaveforms || []).find(
        (w: IPassageWaveformSummary) =>
          w.passageWaveformId === passageWaveformId
      );
      if (passageWaveformId && passage.uid && wf) {
        DeviceService.setPassageWaveformVerification(
          passage.uid,
          passageWaveformId,
          wf.isVerified
        )
          .then(() => {
            if (passage.markerName !== undefined) {
              context.dispatch('verifyMarkerPassageComplete', passage);
            } else {
              context.dispatch('verifyPassageComplete', passage);
            }
          })
          .catch((error: any) => {
            console.error(error);
            context.dispatch('notify', error);
          });
      } else {
        console.error('Missing passageWaveformId or uid');
      }
    },
    verifyMarkerPassageComplete(context: any, passage: any) {
      // Find the active pig run containing a marker with this name and update its icon.
      const pigRuns = context.rootState.pigRun.pigRuns as Record<number, any>;
      for (const pigRunId of Object.keys(pigRuns)) {
        const entry = pigRuns[Number(pigRunId)];
        const marker = entry?.markers?.find(
          (m: any) => m.name === passage.markerName
        );
        if (marker) {
          // Determine if any waveform on this passage is still verified.
          const hasVerified = (passage.passageWaveforms || []).some(
            (wf: any) => wf.isVerified
          );
          if (hasVerified) {
            // Optimistically mark as verified — we know at least one waveform is verified.
            context.commit('UPDATE_MARKER_VERIFICATION', {
              pigRunId: Number(pigRunId),
              pipelineSectionMarkerId: marker.pipelineSectionMarkerId,
              hasVerifiedPassage: true,
            });
          } else {
            // Unverified — we can't know locally if other passages still have verified waveforms.
            // Re-fetch the pig run to get the authoritative hasVerifiedPassage from the API.
            context.dispatch('refreshSinglePigRun', Number(pigRunId));
          }
          break;
        }
      }
    },
    verifyPassageComplete(_context: any, _payload: any) {
      // no state/mutations, this is only used to subscribe to actions in other components
    },
    passageRecieved(_context: any, _payload: any) {
      // no state/mutations, this is only used to subscribe to actions in other components
    },
    mlResultReceived(_context: any, _payload: any) {
      // no state/mutations, this is only used to subscribe to actions in other components
    },
    completeMachineLearningAddPigRunRequest(_context: any) {
      // no state/mutations, this is only used to subscribe to actions in other components
    },
  },
};
